import { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { Plus, Store, MapPin, Palette, Globe, Building, MapPinned, Trash2, Shield, Building2 } from 'lucide-react';
import { countries, getStatesForCountry, getCitiesForState, getTimezoneForCountry } from '@/data/locationData';
import { useAuth } from '@/contexts/AuthContext';

const Outlets = () => {
  const { user: currentUser } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [themeFormData, setThemeFormData] = useState({
    theme_id: '',
    theme_mode: 'LIGHT',
  });
  const [formData, setFormData] = useState({
    org_id: '',
    outlet_name: '',
    outlet_code: '',
    outlet_country: 'IN',
    outlet_state: '',
    outlet_city: '',
    outlet_timezone: 'Asia/Kolkata',
    theme_id: '',
    theme_mode: 'LIGHT',
  });

  // Check if current user is admin (can delete)
  const isAdmin = currentUser?.is_super_admin || currentUser?.is_org_admin;
  const isSuperAdmin = currentUser?.is_super_admin === true;

  // Get states based on selected country
  const availableStates = useMemo(() => {
    return getStatesForCountry(formData.outlet_country);
  }, [formData.outlet_country]);

  // Get cities based on selected country and state
  const availableCities = useMemo(() => {
    return getCitiesForState(formData.outlet_country, formData.outlet_state);
  }, [formData.outlet_country, formData.outlet_state]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [outletsRes, orgsRes, themesRes] = await Promise.all([
        api.getOutlets(),
        api.getOrganizations(),
        api.getThemes(),
      ]);
      setOutlets(outletsRes.data);
      setOrganizations(orgsRes.data);
      setThemes(themesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle country change - reset state and city
  const handleCountryChange = (countryCode) => {
    const timezone = getTimezoneForCountry(countryCode);
    setFormData({
      ...formData,
      outlet_country: countryCode,
      outlet_state: '',
      outlet_city: '',
      outlet_timezone: timezone,
    });
  };

  // Handle state change - reset city
  const handleStateChange = (stateCode) => {
    setFormData({
      ...formData,
      outlet_state: stateCode,
      outlet_city: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate org_id is selected
    if (!formData.org_id) {
      alert('Please select an Organization');
      return;
    }
    
    try {
      // Convert country code to full name for storage
      const country = countries.find(c => c.code === formData.outlet_country);
      const state = availableStates.find(s => s.code === formData.outlet_state);
      
      const submitData = {
        ...formData,
        outlet_country: country?.name || formData.outlet_country,
        outlet_state: state?.name || formData.outlet_state,
      };
      
      await api.createOutlet(submitData);
      setShowForm(false);
      setFormData({
        org_id: '',
        outlet_name: '',
        outlet_code: '',
        outlet_country: 'IN',
        outlet_state: '',
        outlet_city: '',
        outlet_timezone: 'Asia/Kolkata',
        theme_id: '',
        theme_mode: 'LIGHT',
      });
      loadData();
    } catch (error) {
      console.error('Error creating outlet:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create outlet';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const getOrgName = (orgId) => {
    const org = organizations.find((o) => o.org_id === orgId);
    return org ? org.org_name : 'Unknown';
  };

  const getThemeName = (themeId) => {
    const theme = themes.find((t) => t.theme_id === themeId);
    return theme ? theme.theme_name : 'No Theme';
  };

  const handleThemeClick = (outlet) => {
    setSelectedOutlet(outlet);
    setThemeFormData({
      theme_id: outlet.theme_id || '',
      theme_mode: outlet.theme_mode || 'LIGHT',
    });
    setShowThemeModal(true);
  };

  const handleThemeSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.updateOutletTheme(
        selectedOutlet.outlet_id,
        themeFormData.theme_id,
        themeFormData.theme_mode
      );
      setShowThemeModal(false);
      setSelectedOutlet(null);
      loadData();
    } catch (error) {
      console.error('Error updating theme:', error);
      alert('Failed to update theme');
    }
  };

  const handleDeleteOutlet = async (outlet) => {
    setDeleting(true);
    try {
      await api.deleteOutlet(outlet.outlet_id);
      setShowDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error deleting outlet:', error);
      alert(error.response?.data?.detail || 'Failed to delete outlet');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-8" data-testid="outlets-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Outlets</h1>
          <p className="text-gray-600 mt-2">Manage outlet locations</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="create-outlet-btn"
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <Plus size={20} />
          Create Outlet
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg my-8 mx-4" data-testid="outlet-form">
            <h2 className="text-xl font-bold mb-4">Create Outlet</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Organization */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                <select
                  required
                  data-testid="outlet-org-select"
                  value={formData.org_id}
                  onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.org_id} value={org.org_id}>
                      {org.org_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Outlet Name & Code */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outlet Name *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="outlet-name-input"
                    value={formData.outlet_name}
                    onChange={(e) => setFormData({ ...formData, outlet_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., Downtown Branch"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outlet Code *
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="outlet-code-input"
                    value={formData.outlet_code}
                    onChange={(e) => setFormData({ ...formData, outlet_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., OUT001"
                  />
                </div>
              </div>

              {/* Location Section Header */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <MapPinned size={16} className="text-green-600" />
                  Location Details
                </h3>
              </div>

              {/* Country Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Globe size={14} />
                  Country *
                </label>
                <select
                  required
                  data-testid="outlet-country-select"
                  value={formData.outlet_country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* State Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Building size={14} />
                  State/Province *
                </label>
                <select
                  required
                  data-testid="outlet-state-select"
                  value={formData.outlet_state}
                  onChange={(e) => handleStateChange(e.target.value)}
                  disabled={!formData.outlet_country}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {formData.outlet_country ? 'Select State/Province' : 'Select Country First'}
                  </option>
                  {availableStates.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* City Dropdown with Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <MapPin size={14} />
                  City *
                </label>
                {availableCities.length > 0 ? (
                  <select
                    required
                    data-testid="outlet-city-select"
                    value={formData.outlet_city}
                    onChange={(e) => setFormData({ ...formData, outlet_city: e.target.value })}
                    disabled={!formData.outlet_state}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {formData.outlet_state ? 'Select City' : 'Select State First'}
                    </option>
                    {availableCities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    required
                    data-testid="outlet-city-input"
                    value={formData.outlet_city}
                    onChange={(e) => setFormData({ ...formData, outlet_city: e.target.value })}
                    disabled={!formData.outlet_state}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder={formData.outlet_state ? 'Enter city name' : 'Select state first'}
                  />
                )}
                {availableCities.length === 0 && formData.outlet_state && (
                  <p className="text-xs text-gray-500 mt-1">
                    City list not available. Please type the city name.
                  </p>
                )}
              </div>

              {/* Timezone (auto-selected based on country) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <input
                  type="text"
                  data-testid="outlet-timezone-input"
                  value={formData.outlet_timezone}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-selected based on country</p>
              </div>

              {/* Theme Settings */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Palette size={16} className="text-purple-600" />
                  Theme Settings
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
                  <select
                    value={formData.theme_id}
                    onChange={(e) => setFormData({ ...formData, theme_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">No Theme</option>
                    {themes.map((theme) => (
                      <option key={theme.theme_id} value={theme.theme_id}>
                        {theme.theme_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Theme Mode
                  </label>
                  <select
                    value={formData.theme_mode}
                    onChange={(e) => setFormData({ ...formData, theme_mode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="LIGHT">Light</option>
                    <option value="DARK">Dark</option>
                    <option value="AUTO">Auto</option>
                  </select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t mt-4">
                <button
                  type="submit"
                  data-testid="submit-outlet"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Create Outlet
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Theme Assignment Modal */}
      {showThemeModal && selectedOutlet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="theme-modal">
            <h2 className="text-xl font-bold mb-4">
              Change Theme for {selectedOutlet.outlet_name}
            </h2>
            <form onSubmit={handleThemeSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Theme *
                </label>
                <select
                  required
                  data-testid="theme-select"
                  value={themeFormData.theme_id}
                  onChange={(e) => setThemeFormData({ ...themeFormData, theme_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Theme</option>
                  {themes.map((theme) => (
                    <option key={theme.theme_id} value={theme.theme_id}>
                      {theme.theme_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Theme Mode *
                </label>
                <select
                  required
                  data-testid="theme-mode-select"
                  value={themeFormData.theme_mode}
                  onChange={(e) => setThemeFormData({ ...themeFormData, theme_mode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="LIGHT">Light</option>
                  <option value="DARK">Dark</option>
                  <option value="AUTO">Auto</option>
                </select>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Current Theme:</strong> {getThemeName(selectedOutlet.theme_id)}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Current Mode:</strong> {selectedOutlet.theme_mode}
                </p>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  data-testid="submit-theme-change"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Update Theme
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowThemeModal(false);
                    setSelectedOutlet(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {outlets.map((outlet) => (
            <div
              key={outlet.outlet_id}
              data-testid={`outlet-card-${outlet.outlet_code}`}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <Store className="text-green-600" size={24} />
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    outlet.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {outlet.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{outlet.outlet_name}</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>
                  <span className="font-medium">Code:</span> {outlet.outlet_code}
                </p>
                <p>
                  <span className="font-medium">Organization:</span> {getOrgName(outlet.org_id)}
                </p>
                {(outlet.outlet_city || outlet.outlet_state || outlet.outlet_country) && (
                  <p className="flex items-center gap-1">
                    <MapPin size={14} />
                    {[outlet.outlet_city, outlet.outlet_state, outlet.outlet_country]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                <p>
                  <span className="font-medium">Theme:</span> {getThemeName(outlet.theme_id)}
                </p>
                <p>
                  <span className="font-medium">Mode:</span>{' '}
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                    {outlet.theme_mode}
                  </span>
                </p>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleThemeClick(outlet)}
                  data-testid={`change-theme-${outlet.outlet_code}`}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                >
                  <Palette size={16} />
                  Change Theme
                </button>
                {/* Delete button - Admin only */}
                {isAdmin && (
                  <button
                    onClick={() => setShowDeleteConfirm(outlet)}
                    data-testid={`delete-outlet-${outlet.outlet_code}`}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm"
                    title={isSuperAdmin ? "Delete outlet (SUPER_ADMIN)" : "Delete outlet (your org only)"}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              {/* Scope label for admins */}
              {isAdmin && (
                <div className="mt-2 flex justify-end">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 flex items-center gap-1">
                    {isSuperAdmin ? (
                      <>
                        <Shield size={8} />
                        Scope: All Orgs
                      </>
                    ) : (
                      <>
                        <Building2 size={8} />
                        Scope: Your Org
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Outlet Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete Outlet</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                Are you sure you want to delete <strong>{showDeleteConfirm.outlet_name}</strong>?
              </p>
              <ul className="text-xs text-red-600 mt-2 space-y-1">
                <li>• All user-outlet role assignments will be removed</li>
                <li>• Outlet-specific data will be deleted</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteOutlet(showDeleteConfirm)}
                disabled={deleting}
                data-testid="confirm-delete-outlet-btn"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Outlet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {outlets.length === 0 && !loading && (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <Store className="mx-auto mb-4 text-gray-400" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No outlets yet</h3>
          <p className="text-gray-600">Create your first outlet to get started</p>
        </div>
      )}
    </div>
  );
};

export default Outlets;
